import { useNavigate } from 'react-router-dom';
import { useEffect, useState, Suspense } from 'react';
import { lazyRetry as lazy } from '@/lib/lazyRetry';
import { ArrowRight, Radar, TrendingUp, Pause, Users, HeartPulse, AlertTriangle, Zap, CheckCircle2, Instagram, Linkedin } from 'lucide-react';
import { siMeta, siTiktok, siGoogleads } from 'simple-icons';
import { track } from './track';
import { openAssistant } from '@/components/assistant/VisitorAssistant';
import { HeroInvite } from '@/components/assistant/HeroInvite';

const HeroBotInteractive = lazy(() => import('./HeroBot'));

/* Floating ambient particles — light purple/cyan dots drifting upward (pure CSS) */
export const ParticleField = ({ count = 22 }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => {
      const size = 2 + (i % 3);
      const left = (i * 37 + 11) % 100;
      const delay = (i * 1.7) % 12;
      const dur = 14 + (i % 9);
      const tint = i % 3 === 0 ? 'rgba(139,92,246,0.5)' : i % 3 === 1 ? 'rgba(103,232,249,0.4)' : 'rgba(217,70,239,0.35)';
      return (
        <span key={i} className="absolute rounded-full"
          style={{
            width: size, height: size, left: `${left}%`, bottom: '-4px',
            background: tint, boxShadow: `0 0 ${size * 3}px ${tint}`,
            animation: `vel-particle ${dur}s linear ${delay}s infinite`,
          }} />
      );
    })}
    <style>{`@keyframes vel-particle { 0% { transform: translateY(0); opacity: 0; } 8% { opacity: 1; } 85% { opacity: 0.6; } 100% { transform: translateY(-105vh); opacity: 0; } }`}</style>
  </div>
);

/* Lightweight CSS-only bot — the default (and what mobile always gets). No JS animation library. */
const StaticBot = () => (
  <div className="inline-flex flex-col items-center pointer-events-none select-none vel-bot-float" data-testid="hero-ai-bot">
    <div className="relative flex flex-col items-center">
      <div className="absolute -inset-8 rounded-full bg-gradient-to-br from-violet-500/30 to-cyan-500/20 blur-2xl" />
      <div className="relative">
        <div className="absolute left-1/2 -translate-x-1/2 -top-[22px] flex flex-col items-center">
          <span className="h-2 w-2 rounded-full bg-cyan-300 shadow-[0_0_12px_rgba(103,232,249,0.9)] vel-antenna" />
          <span className="h-3.5 w-[2.5px] rounded-full bg-gradient-to-b from-cyan-400/80 to-violet-500/40" />
        </div>
        <div className="relative h-[86px] w-[110px] rounded-[1.7rem] bg-[#0d0d16]/95 border border-violet-400/30 backdrop-blur-xl shadow-2xl shadow-violet-500/25 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0" style={{ background: 'conic-gradient(from 0deg, transparent, rgba(139,92,246,0.22), transparent 30%)', animation: 'home-orbit-spin 6s linear infinite' }} />
          <div className="relative h-[58px] w-[86px] rounded-[1.2rem] bg-gradient-to-b from-[#12121f] to-[#0a0a14] border border-white/[0.06] flex flex-col items-center justify-center">
            <div className="flex items-center gap-4">
              {[0, 1].map((i) => (
                <span key={i} className="h-[18px] w-[12px] rounded-full bg-gradient-to-b from-cyan-300 to-violet-400 shadow-[0_0_10px_rgba(103,232,249,0.8)] vel-blink" />
              ))}
            </div>
            <svg viewBox="0 0 40 12" className="mt-1.5 w-6 h-2">
              <path d="M4,3 Q20,13 36,3" fill="none" stroke="#67e8f9" strokeWidth="3.5" strokeLinecap="round" opacity="0.85" />
            </svg>
          </div>
        </div>
      </div>
      <span className="h-1.5 w-6 rounded-b-md bg-[#12121f] border-x border-b border-violet-400/20" />
      <div className="relative mt-0.5 h-[42px] w-[72px] rounded-2xl bg-[#0d0d16]/95 border border-violet-400/25 backdrop-blur-xl shadow-xl shadow-violet-500/15 flex items-center justify-center">
        <span className="h-4 w-4 rounded-full bg-gradient-to-br from-cyan-400 to-violet-500 shadow-[0_0_14px_rgba(139,92,246,0.8)] vel-glow" />
      </div>
    </div>
  </div>
);

/* Renders the light CSS bot by default; upgrades to the interactive framer-motion bot
   only on desktop pointers (and only after mount), so mobile stays fast. */
const HeroBotMount = () => {
  const [fancy, setFancy] = useState(false);
  useEffect(() => {
    try {
      const fine = window.matchMedia('(pointer:fine)').matches;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (fine && !reduce) setFancy(true);
    } catch (_e) { /* keep static */ }
  }, []);
  if (!fancy) return <StaticBot />;
  return <Suspense fallback={<StaticBot />}><HeroBotInteractive /></Suspense>;
};

export const Hero = () => {
  const navigate = useNavigate();
  const toAnalyzer = () => {
    track('hero_analyzer_clicked');
    document.querySelector('#demand-analyzer')?.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-20 px-5 sm:px-8 overflow-hidden">
      {/* grid + purple ambiance */}
      <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(139,92,246,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.05) 1px, transparent 1px)', backgroundSize: '52px 52px', maskImage: 'radial-gradient(ellipse 85% 70% at 50% 35%, black 35%, transparent 100%)' }} />
      <div className="absolute top-[-180px] left-1/2 -translate-x-1/2 w-[950px] h-[540px] bg-violet-600/[0.13] rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute top-[180px] right-[-140px] w-[420px] h-[420px] bg-fuchsia-600/[0.08] rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute top-[320px] left-[-140px] w-[380px] h-[380px] bg-cyan-500/[0.06] rounded-full blur-[120px] pointer-events-none" />
      <ParticleField />

      <div className="max-w-4xl mx-auto text-center relative">
        <div className="mb-6 vel-fade-up relative">
          <button type="button" onClick={() => { track('hero_bot_clicked'); openAssistant('text', 'hero_bot'); }} data-testid="hero-bot-button"
            aria-label="Open Veluro AI assistant" title="Ask Veluro"
            className="inline-flex rounded-3xl cursor-pointer hover:scale-[1.04] active:scale-[0.98] transition-transform motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60">
            <HeroBotMount />
          </button>
          <HeroInvite />
        </div>
        <div className="vel-fade-up vel-delay-1">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur-md mb-7">
            <Zap className="h-3.5 w-3.5 text-fuchsia-400" />
            <span className="text-xs font-semibold text-white/60 tracking-[0.18em] uppercase">Your AI-Powered Growth Team</span>
          </div>
          <h1 data-testid="hero-headline" className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[1.05]">
            Find Customers. Launch Campaigns. Grow Your Business.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-fuchsia-400 to-fuchsia-300">Let AI Handle the Work.</span>
          </h1>
          <p data-testid="hero-subheadline" className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed font-light max-w-3xl mx-auto">
            Veluro finds high-intent prospects, creates and manages your marketing, follows up with leads, and continuously looks for ways to grow your business — all from one AI-powered platform.
          </p>
          <div className="mt-8 flex justify-center vel-fade-up vel-delay-2" data-testid="hero-free-trial-badge">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/[0.08] px-4 py-2 shadow-[0_0_28px_rgba(16,185,129,0.18)] backdrop-blur-md">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
              <span className="text-sm sm:text-base font-bold text-white tracking-tight leading-none">
                7-Day Free Trial <span className="text-emerald-300">— $0 Today</span>
              </span>
            </div>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3.5">
            <button data-testid="hero-trial-btn" onClick={() => { track('hero_trial_clicked'); navigate('/pricing'); }}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:brightness-110 text-white font-bold px-9 py-4 text-base shadow-2xl shadow-violet-500/35 hover:scale-[1.03] active:scale-[0.98] transition-[transform,filter]">
              Start My Free Trial <ArrowRight className="h-5 w-5" />
            </button>
            <button data-testid="hero-analyzer-btn" onClick={toAnalyzer}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full backdrop-blur-md bg-white/5 border border-white/10 hover:bg-white/10 hover:border-cyan-400/30 text-white/85 font-semibold px-8 py-4 text-base hover:scale-[1.03] transition-[transform,background-color,border-color]">
              <Radar className="h-4.5 w-4.5 text-cyan-300" /> Analyze My Demand Free
            </button>
          </div>
          <p data-testid="hero-trust-line" className="mt-6 text-sm text-white/35">
            $0 today <span className="mx-1.5 text-white/20">•</span> Set up in minutes <span className="mx-1.5 text-white/20">•</span> Cancel anytime
          </p>
        </div>
      </div>
    </section>
  );
};

const SiIcon = ({ icon, className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true"><path d={icon.path} /></svg>
);

const CHANNELS = [
  { slug: 'meta', name: 'Meta', render: (c) => <SiIcon icon={siMeta} className={c} /> },
  { slug: 'instagram', name: 'Instagram', render: (c) => <Instagram className={c} strokeWidth={2} /> },
  { slug: 'linkedin', name: 'LinkedIn', render: (c) => <Linkedin className={c} strokeWidth={2} /> },
  { slug: 'tiktok', name: 'TikTok', render: (c) => <SiIcon icon={siTiktok} className={c} /> },
  { slug: 'google-ads', name: 'Google Ads', render: (c) => <SiIcon icon={siGoogleads} className={c} /> },
];

/* "Works with" proof strip — the channels Veluro actually runs, right under the hero. */
export const WorksWith = () => (
  <section aria-label="Channels Veluro runs" data-testid="hero-works-with" className="relative px-5 sm:px-8 pb-14 sm:pb-16 -mt-2">
    <div className="max-w-5xl mx-auto vel-fade-up vel-delay-3">
      <div className="flex items-center gap-4 mb-6">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-white/10" />
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/40 text-center whitespace-nowrap" data-testid="works-with-label">
          Veluro runs your ads &amp; posts on
        </p>
        <div className="h-px flex-1 bg-gradient-to-l from-transparent via-white/10 to-white/10" />
      </div>
      <ul className="flex flex-wrap items-center justify-center gap-x-8 sm:gap-x-12 gap-y-4">
        {CHANNELS.map((c) => (
          <li key={c.slug} data-testid={`works-with-${c.slug}`}
            className="group inline-flex items-center gap-2.5 text-white/55 hover:text-white transition-colors duration-200">
            {c.render('h-5 w-5 sm:h-[22px] sm:w-[22px] shrink-0 opacity-80 group-hover:opacity-100 transition-opacity')}
            <span className="text-sm sm:text-[15px] font-semibold tracking-tight">{c.name}</span>
          </li>
        ))}
      </ul>
      <p className="mt-5 text-center text-[13px] text-white/40" data-testid="works-with-caption">
        Meta, Instagram, LinkedIn, TikTok and Google Ads — connected once, run by Veluro every day.
      </p>
    </div>
  </section>
);

/* Live cards render their FINAL values directly in the initial markup — no count-up from zero. */
export const LiveCards = () => {
  const toAnalyzer = () => document.querySelector('#demand-analyzer')?.scrollIntoView({ behavior: 'smooth' });
  const card = 'rounded-2xl backdrop-blur-xl bg-white/[0.05] border p-6 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5';
  return (
    <section className="px-5 sm:px-8 pt-6 sm:pt-10 pb-20 sm:pb-28 relative" aria-label="Veluro working live">
      <div className="absolute bottom-0 left-1/3 w-[500px] h-[300px] bg-violet-600/[0.06] rounded-full blur-[120px] pointer-events-none" />
      <div className="max-w-6xl mx-auto relative">
        <h2 className="text-center text-lg md:text-lg font-semibold text-white/85 font-['Outfit'] mb-2">
          Your business doesn't stop when you do. Neither does Veluro.
        </h2>
        <p className="text-center text-[12px] text-white/30 mb-8">Live product example</p>
        <div className="grid md:grid-cols-3 gap-4 sm:gap-5">
          <div
            data-testid="live-card-ads" className={`${card} vel-fade-up border-violet-500/20 hover:border-violet-400/45 hover:shadow-[0_18px_50px_rgba(139,92,246,0.18)]`}>
            <div className="flex items-center gap-2 text-[11px] font-bold tracking-widest text-violet-300 mb-4">
              <TrendingUp className="h-3.5 w-3.5" /> MANAGING YOUR ADS
            </div>
            <p className="text-4xl font-black text-white font-['Outfit']">3.4× <span className="text-lg text-white/50 font-semibold">ROAS</span></p>
            <p className="text-[13px] text-emerald-400 font-semibold mt-1">↑ 18% this month</p>
            <div className="mt-5 rounded-xl bg-amber-500/[0.07] border border-amber-500/25 p-3.5">
              <p className="flex items-center gap-1.5 text-[13px] text-white/85 font-medium"><Pause className="h-3.5 w-3.5 text-amber-300" /> Veluro just paused a losing ad.</p>
              <p className="text-[12.5px] text-white/45 mt-1">Saving approximately <span className="text-amber-300 font-bold">$85/day</span></p>
            </div>
          </div>

          <div
            data-testid="live-card-buyers" className={`${card} vel-fade-up vel-delay-1 border-cyan-500/20 hover:border-cyan-400/45 hover:shadow-[0_18px_50px_rgba(34,211,238,0.14)]`}>
            <div className="flex items-center gap-2 text-[11px] font-bold tracking-widest text-cyan-300 mb-4">
              <Users className="h-3.5 w-3.5" /> FINDING HIGH-INTENT BUYERS
            </div>
            <p className="text-4xl font-black text-white font-['Outfit']">22,847 <span className="text-lg text-white/50 font-semibold">buyers found</span></p>
            <p className="text-[13px] text-cyan-300 font-semibold mt-1">14,012 identified as hot buyers</p>
            <p className="text-[12.5px] text-white/45 mt-5">Veluro detected new buying activity in your market.</p>
            <button onClick={toAnalyzer} data-testid="live-card-buyers-cta" className="mt-3 text-[13px] font-semibold text-cyan-300 hover:text-cyan-200 transition-colors">View Buyers →</button>
          </div>

          <div
            data-testid="live-card-health" className={`${card} vel-fade-up vel-delay-2 border-emerald-500/20 hover:border-emerald-400/45 hover:shadow-[0_18px_50px_rgba(52,211,153,0.14)]`}>
            <div className="flex items-center gap-2 text-[11px] font-bold tracking-widest text-emerald-300 mb-4">
              <HeartPulse className="h-3.5 w-3.5" /> BUSINESS HEALTH
            </div>
            <p className="text-4xl font-black text-white font-['Outfit']">78 <span className="text-lg text-white/50 font-semibold">/ 100</span></p>
            <p className="text-[13px] text-white/45 font-medium mt-1">Overall Business Health</p>
            <div className="mt-5 rounded-xl bg-rose-500/[0.07] border border-rose-500/25 p-3.5">
              <p className="flex items-center gap-1.5 text-[13px] text-white/85 font-medium"><AlertTriangle className="h-3.5 w-3.5 text-rose-300" /> Cash-flow risk detected</p>
              <p className="text-[12.5px] text-white/45 mt-1">Projected impact: <span className="text-rose-300 font-bold">21 days</span></p>
            </div>
            <button onClick={toAnalyzer} data-testid="live-card-health-cta" className="mt-3 text-[13px] font-semibold text-emerald-300 hover:text-emerald-200 transition-colors">See Recommendation →</button>
          </div>
        </div>
        <p className="text-center text-[14px] text-white/45 mt-8">
          While you're running your business, <span className="text-white/80 font-medium">Veluro is running your growth engine.</span>
        </p>
      </div>
    </section>
  );
};
